import React, { useEffect, useState } from 'react';
import { Landmark, Loader2, ArrowRightLeft } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { RateioAncoraSaldoUg, RateioAncoraTransferencia, RateioUnidadeGeradora, UnidadeConsumidora } from '../../../types';
import TransferenciaTitularidadeWizard from './TransferenciaTitularidadeWizard';
import AncoraTransferenciasAtivasCard from './AncoraTransferenciasAtivasCard';
import { buscarUltimoPercentualPorUc, UltimoPercentualUc } from './consumoHistorico';

type BancoCreditosUc = Pick<
  UnidadeConsumidora,
  'id' | 'numero_uc' | 'outro_numero_uc' | 'nome_cliente' | 'titular_fatura' | 'rateio_unidade_geradora_id'
> & {
  rateio_unidades_geradoras: (RateioUnidadeGeradora & { distribuidora_config?: { nome: string; dia_leitura_padrao: number | null } | null }) | null;
};

const AncoraView: React.FC = () => {
  const [ucs, setUcs] = useState<BancoCreditosUc[]>([]);
  const [saldos, setSaldos] = useState<Map<string, number>>(new Map());
  const [percentuais, setPercentuais] = useState<Map<number, UltimoPercentualUc>>(new Map());
  const [transferenciasAtivas, setTransferenciasAtivas] = useState<RateioAncoraTransferencia[]>([]);
  const [loading, setLoading] = useState(true);
  const [wizardUc, setWizardUc] = useState<BancoCreditosUc | null>(null);

  const carregar = async () => {
    setLoading(true);
    const { data: ucsData } = await supabase
      .from('unidades_consumidoras')
      .select(
        'id, numero_uc, outro_numero_uc, nome_cliente, titular_fatura, rateio_unidade_geradora_id, rateio_unidades_geradoras(*, distribuidora_config(nome, dia_leitura_padrao))'
      )
      .eq('rateio_e_banco_creditos', true)
      .order('numero_uc');

    const ucsTyped = (ucsData || []) as unknown as BancoCreditosUc[];
    setUcs(ucsTyped);

    const ugIds = Array.from(new Set(ucsTyped.map(u => u.rateio_unidade_geradora_id).filter((id): id is string => !!id)));

    const [{ data: saldosData }, { data: transferenciasData }, percentuaisMap] = await Promise.all([
      ugIds.length > 0
        ? supabase.from('rateio_ancora_saldo_ug').select('rateio_unidade_geradora_id, saldo_ancora_kwh').in('rateio_unidade_geradora_id', ugIds)
        : Promise.resolve({ data: [] as Pick<RateioAncoraSaldoUg, 'rateio_unidade_geradora_id' | 'saldo_ancora_kwh'>[] }),
      ugIds.length > 0
        ? supabase
            .from('rateio_ancora_transferencias')
            .select('*, rateio_unidades_geradoras(nome), unidades_consumidoras(numero_uc, nome_cliente)')
            .eq('status', 'ativa')
            .in('rateio_unidade_geradora_id', ugIds)
            .order('data_retorno_agendada')
        : Promise.resolve({ data: [] as RateioAncoraTransferencia[] }),
      buscarUltimoPercentualPorUc(ucsTyped.map(u => u.id)),
    ]);

    const saldosTyped = (saldosData || []) as Pick<RateioAncoraSaldoUg, 'rateio_unidade_geradora_id' | 'saldo_ancora_kwh'>[];
    setSaldos(new Map(saldosTyped.map(s => [s.rateio_unidade_geradora_id, s.saldo_ancora_kwh])));
    setTransferenciasAtivas((transferenciasData || []) as RateioAncoraTransferencia[]);
    setPercentuais(percentuaisMap);
    setLoading(false);
  };

  useEffect(() => {
    carregar();
  }, []);

  const formatKwh = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatPercent = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const emprestadoPorUg = new Map<string, number>();
  for (const t of transferenciasAtivas) {
    emprestadoPorUg.set(t.rateio_unidade_geradora_id, (emprestadoPorUg.get(t.rateio_unidade_geradora_id) || 0) + t.montante_kwh);
  }
  const saldoDisponivel = (ugId: string) => (saldos.get(ugId) || 0) - (emprestadoPorUg.get(ugId) || 0);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="animate-spin text-gray-400" size={28} />
      </div>
    );
  }

  if (ucs.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-16 text-center">
        <Landmark className="mx-auto text-gray-300 dark:text-gray-600 mb-3" size={40} />
        <p className="text-gray-500 dark:text-gray-400">Nenhuma UC Banco de Créditos cadastrada ainda.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-100 dark:border-gray-700">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white font-poppins flex items-center gap-2">
            <Landmark className="text-primary" size={20} /> Gestão da Unidade Âncora
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            UCs "Banco de Créditos" que absorvem o percentual excedente do rateio de cada UG.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase border-b border-gray-200 dark:border-gray-700">
                <th className="py-2 px-6">UC / Nova UC</th>
                <th className="py-2 px-6">Titular</th>
                <th className="py-2 px-6">UG Vinculada</th>
                <th className="py-2 px-6">% Livre</th>
                <th className="py-2 px-6">Saldo Armazenado</th>
                <th className="py-2 px-6">Data de Leitura</th>
                <th className="py-2 px-6 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {ucs.map(uc => {
                const ug = uc.rateio_unidades_geradoras;
                const ugId = uc.rateio_unidade_geradora_id;
                const emprestado = ugId ? emprestadoPorUg.get(ugId) || 0 : 0;
                const ultimoPercentual = percentuais.get(uc.id);
                return (
                  <tr key={uc.id} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-3 px-6">
                      <span className="font-medium text-gray-900 dark:text-gray-100">{uc.numero_uc}</span>
                      {uc.outro_numero_uc && (
                        <span className="block text-xs text-gray-400 dark:text-gray-500">Nova UC: {uc.outro_numero_uc}</span>
                      )}
                    </td>
                    <td className="py-3 px-6 text-gray-600 dark:text-gray-300">{uc.titular_fatura || uc.nome_cliente || '—'}</td>
                    <td className="py-3 px-6">
                      {ug ? (
                        <>
                          <span className="text-gray-900 dark:text-gray-100 font-medium">{ug.nome}</span>
                          <span className="block text-xs text-gray-400 dark:text-gray-500">{ug.distribuidora_config?.nome || '—'}</span>
                        </>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-medium px-2.5 py-1">
                          Não vinculada
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-6 text-gray-900 dark:text-gray-100">
                      {ultimoPercentual ? (
                        <>
                          {formatPercent(ultimoPercentual.percent)}%
                          <span className="block text-xs font-normal text-gray-400 dark:text-gray-500">{ultimoPercentual.mesReferencia}</span>
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-3 px-6 text-gray-900 dark:text-gray-100 font-medium">
                      {ugId ? (
                        <>
                          {formatKwh(saldos.get(ugId) || 0)} kWh
                          {emprestado > 0 && (
                            <span className="block text-xs font-normal text-amber-600 dark:text-amber-400">{formatKwh(emprestado)} kWh emprestado</span>
                          )}
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-3 px-6 text-gray-600 dark:text-gray-300">
                      {ug?.distribuidora_config?.dia_leitura_padrao ? `Dia ${ug.distribuidora_config.dia_leitura_padrao}` : '—'}
                    </td>
                    <td className="py-3 px-6 text-right">
                      {ug && (
                        <button onClick={() => setWizardUc(uc)} className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
                          <ArrowRightLeft size={14} /> Transferir Titularidade
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <AncoraTransferenciasAtivasCard transferencias={transferenciasAtivas} onChanged={carregar} />

      {wizardUc?.rateio_unidades_geradoras && (
        <TransferenciaTitularidadeWizard
          ug={wizardUc.rateio_unidades_geradoras}
          saldoDisponivelKwh={saldoDisponivel(wizardUc.rateio_unidade_geradora_id!)}
          onClose={() => setWizardUc(null)}
          onConfirmado={carregar}
        />
      )}
    </div>
  );
};

export default AncoraView;
